import { GuidedFlowSchema } from '@/schemas/storage';
import { PropertyMessage } from '@/components/ai/types';
import { usePlanStore } from '@/stores/ai/plan-store';
import { usePropertyApplication } from './usePropertyApplication';
import { useCanvasTextParser } from './useCanvasTextParser';
import { useAgentStream } from './useAgentStream';
import { useCanvasPlacement } from './useCanvasPlacement';
import { useSlashCommands } from './useSlashCommands';
import { useAgentRouting } from './useAgentRouting';
import { usePreBackendRouting } from './usePreBackendRouting';
import { useAgentPropertyFlow } from './useAgentPropertyFlow';
import { useAICommandPermissions } from './agentCommandPermissions';
import type { UseAgentMessagingDeps, UseAgentMessagingReturn } from './agentMessagingTypes';

export function useAgentMessaging(deps: UseAgentMessagingDeps): UseAgentMessagingReturn {
  const {
    inputValue, setInputValue,
    isLoading, setIsLoading,
    setError,
    messages, setMessages,
    currentGenerationMessageId, setCurrentGenerationMessageId,
    setShowAutocomplete,
    component, multipleComponents, isCanvas,
    agentThreadIdRef, agentVisualThreadIdRef, userAgentThreadIdRef,
    storeInitialized, resetAgentThread,
    streamingActionCountRef, pendingConfirmationActionsRef,
    setStreamingSteps, setStreamingMessage, setIsStreamingActive,
    setMultiPageProgress, setPendingConfirmation, setPendingChangeSummary,
    setProactiveSuggestions,
    agentStatus, setAgentStatus,
    project, tokens, storePages, currentPageId, components,
    updateComponent, addComponent, generatePageFromDescription,
    selectMultipleComponents, resizeComponent, moveComponent,
    canvasSettings, updateCanvasBackground, updateGridSettings,
    updateCanvasLayout, updateCanvasTheme, updateResponsiveSettings,
    aiGenerationStage, aiGenerationError, aiGenerationMetadata,
    setPendingClarification,
    addToHistory, getLastSuccessfulCommand,
    userCommunity, tradeScope,
    isSlashCommand, executeSlashCommand,
    planOrchestrator,
    dataDiscovery,
    persistMessage, generateMessageId,
    currentMentionContextRef, pendingAutoSendRef, inputRef,
  } = deps;

  // ── Extracted hooks ──────────────────────────────────────────────────────────
  const commandPermissionGuard = useAICommandPermissions(project);

  const { applyPropertyChanges } = usePropertyApplication({
    component,
    multipleComponents,
    isCanvas,
    commandPermissionGuard,
    setMessages,
    updateComponent,
    resizeComponent,
    updateCanvasBackground,
    updateGridSettings,
    updateCanvasLayout,
    updateCanvasTheme,
    updateResponsiveSettings,
  });

  const { parseCanvasChanges } = useCanvasTextParser({
    canvasSettings,
  });

  const { buildStreamingCallbacks, handleStopGeneration } = useAgentStream({
    agentVisualThreadIdRef,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    setIsLoading,
    setStreamingSteps,
    setStreamingMessage,
    setIsStreamingActive,
    setMultiPageProgress,
    setPendingConfirmation,
    setPendingChangeSummary,
    setProactiveSuggestions,
    project,
    setMessages,
    persistMessage,
    generateMessageId,
  });

  const { handlePlaceComponent, handlePlaceComponents, handleApplyLayout } = useCanvasPlacement({
    project,
    component,
    components,
    multipleComponents,
    currentMentionContextRef,
    addComponent,
    moveComponent,
    selectMultipleComponents,
    setMessages,
    setError,
    generateMessageId,
    commandPermissionGuard,
  });

  const { handleSlashCommand, handleNaturalLanguageCreationCommand } = useSlashCommands({
    component,
    multipleComponents,
    isCanvas,
    inputRef,
    isSlashCommand,
    executeSlashCommand,
    updateComponent,
    setShowAutocomplete,
    setMessages,
    setIsLoading,
    setInputValue,
    setError,
    persistMessage,
    generateMessageId,
    agentVisualThreadIdRef,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    getLastSuccessfulCommand,
    storePages,
    currentPageId,
    dataDiscovery,
    planOrchestrator,
    setProactiveSuggestions,
    applyPropertyChanges,
    buildStreamingCallbacks,
    commandPermissionGuard,
  });

  const { handleRoutedMessage, routeToAgent } = useAgentRouting({
    component,
    isCanvas,
    messages,
    setMessages,
    setInputValue,
    setIsLoading,
    agentThreadIdRef,
    agentVisualThreadIdRef,
    userAgentThreadIdRef,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    setStreamingSteps,
    setStreamingMessage,
    setIsStreamingActive,
    setMultiPageProgress,
    project,
    storePages,
    agentStatus,
    userCommunity,
    tradeScope,
    persistMessage,
    generateMessageId,
    buildStreamingCallbacks,
  });

  const {
    handleLocalPreBackendCommands,
    handleIntentRoutedPreBackendMessage,
  } = usePreBackendRouting({
    component,
    multipleComponents,
    isCanvas,
    agentStatus,
    project,
    storePages,
    setMessages,
    setInputValue,
    setIsLoading,
    setCurrentGenerationMessageId,
    setPendingClarification,
    persistMessage,
    generateMessageId,
    generatePageFromDescription,
    addToHistory,
    isSlashCommand,
    executeSlashCommand,
    applyPropertyChanges,
    routeToAgent,
    updateComponent,
    commandPermissionGuard,
  });

  const { handlePropertyFlow } = useAgentPropertyFlow({
    inputValue,
    setInputValue,
    setIsLoading,
    setError,
    setMessages,
    component,
    multipleComponents,
    isCanvas,
    project,
    tokens,
    updateComponent,
    resizeComponent,
    moveComponent,
    canvasSettings,
    setPendingClarification,
    addToHistory,
    persistMessage,
    generateMessageId,
    currentMentionContextRef,
    parseCanvasChanges,
    applyPropertyChanges,
    handlePlaceComponent,
    handlePlaceComponents,
    commandPermissionGuard,
  });

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    // Mark that user has used the AI chat (for hiding welcome message on future visits)
    localStorage.setItem('terramatrix_ai_chat_used', 'true');

    // Save the user's input for fallback parsing
    const userInput = inputValue.trim();

    // Special slash command: /plan activates Plan Mode
    if (userInput.toLowerCase() === '/plan') {
      usePlanStore.getState().activate();
      setInputValue('');
      const systemMessage: PropertyMessage = {
        id: generateMessageId(),
        content: "Plan Mode activated. Describe what you'd like to build and I'll help you plan it out before executing.",
        role: 'assistant',
        timestamp: new Date(),
        metadata: { source: 'plan-mode' },
      };
      setMessages(prev => [...prev, systemMessage]);
      persistMessage(systemMessage);
      return;
    }

    // Guided flow: intercept "what's next?" and show guided app creation steps
    const NEXT_STEP_PATTERNS = /\b(what'?s?\s*next|next\s*steps?|now\s*what|what\s*should\s*i\s*do|what\s*do\s*i\s*do\s*now)\b/i;
    if (NEXT_STEP_PATTERNS.test(userInput)) {
      try {
        // Check sessionStorage first (set during knowledge creation), then fall back to generic
        const guidedFlow = sessionStorage.getItem('terramatrix_guided_flow');
        const flowResult = guidedFlow ? GuidedFlowSchema.safeParse(JSON.parse(guidedFlow)) : null;
        const parsed = flowResult?.success ? flowResult.data : null;
        const domain = parsed?.domain || 'your app';
        const kbTitle = parsed?.kbTitle || 'your knowledge base';
        let lastStep = parsed?.lastStep || 'knowledge';
        // Preserve the most-advanced step — dynamic detection below may upgrade
        // 'knowledge' → 'data', but should never downgrade 'pages' → 'data'.
        const originalLastStep = lastStep;

        // Dynamic check: if lastStep is 'knowledge', check if schemas were created (e.g. via UserAgent)
        if (lastStep === 'knowledge' && originalLastStep !== 'pages') {
          try {
            const { schemaApi } = await import('@/services/api/schemaApi');
            const schemas = await schemaApi.listSchemas({ limit: 1, status: 'published' });
            if (schemas.data?.length > 0) {
              lastStep = 'data';
              // Update sessionStorage so future checks are instant
              sessionStorage.setItem('terramatrix_guided_flow', JSON.stringify({ ...parsed, lastStep: 'data' }));
            }
          } catch { /* non-fatal — use sessionStorage value */ }
        }

        setInputValue('');
        const userMsg: PropertyMessage = {
          id: generateMessageId(), content: userInput, role: 'user', timestamp: new Date(),
        };
        setMessages(prev => [...prev, userMsg]);
        persistMessage(userMsg);

        let content = '';
        let suggestions: string[] = [];
        if (lastStep === 'knowledge') {
          content =
            `Here's the recommended flow for building your app:\n\n` +
            `1. **Knowledge Base** — Done\n` +
            `2. **Data Schema** — Define the data your app will manage\n` +
            `3. **Pages & UI** — Design your interface\n` +
            `4. **Workflows** — Set up agents, automations and business logic\n\n` +
            `The next step is to **define your data schema** — the structures your app will use to store and manage information.`;
          suggestions = [
            `Create a data schema for ${domain}`,
            `Build a page using ${kbTitle}`,
            `Set up a workflow for ${domain}`,
            `Fill knowledge gaps first`,
          ];
        } else if (lastStep === 'data') {
          content =
            `Great progress! Here's where you're at:\n\n` +
            `1. ~~Knowledge Base~~ — Done\n` +
            `2. ~~Data Schema~~ — Done\n` +
            `3. **Pages & UI** — Design your interface\n` +
            `4. **Workflows** — Set up agents, automations and business logic\n\n` +
            `The next step is to **design your pages and UI** — the screens your users will interact with.`;
          suggestions = [
            `Build a dashboard page for ${domain}`,
            `Create a data entry form for ${domain}`,
            `Set up a workflow for ${domain}`,
          ];
        } else if (lastStep === 'pages') {
          content =
            `Excellent progress! Here's where you're at:\n\n` +
            `1. ~~Knowledge Base~~ — Done\n` +
            `2. ~~Data Schema~~ — Done\n` +
            `3. ~~Pages & UI~~ — Done\n` +
            `4. **Workflows** — Set up agents, automations and business logic\n\n` +
            `The next step is to **set up workflows** — automations, triggers, and business rules.`;
          suggestions = [
            `Create a workflow for ${domain}`,
            `Set up automations for ${domain}`,
            `Add more pages`,
          ];
        } else {
          content =
            `Here's the recommended flow for building your app:\n\n` +
            `1. **Knowledge Base** — Tell me about your idea so I understand the domain\n` +
            `2. **Data Schema** — Define the data your app will manage\n` +
            `3. **Pages & UI** — Design your interface\n` +
            `4. **Workflows** — Set up agents, automations and business logic\n\n` +
            `What would you like to work on?`;
          suggestions = [
            `Create a data schema for ${domain}`,
            `Build a page using ${kbTitle}`,
            `Set up a workflow for ${domain}`,
          ];
        }

        const guidedMsg: PropertyMessage = {
          id: generateMessageId(), role: 'assistant', timestamp: new Date(),
          metadata: { source: 'agent' }, content, suggestions,
        };
        setMessages(prev => [...prev, guidedMsg]);
        persistMessage(guidedMsg);
        return;
      } catch { /* fall through to normal chat */ }
    }

    const earlyCommandResult = await handleSlashCommand(userInput);
    if (earlyCommandResult.handled) {
      return;
    }

    const localPreBackendResult = await handleLocalPreBackendCommands(userInput);
    if (localPreBackendResult.handled) {
      return;
    }

    // ===================================================================
    const routedMessageResult = await handleRoutedMessage(userInput);
    if (routedMessageResult.handled) {
      return;
    }

    const naturalLanguageCreationResult = await handleNaturalLanguageCreationCommand(userInput);
    if (naturalLanguageCreationResult.handled) {
      return;
    }

    const preBackendResult = await handleIntentRoutedPreBackendMessage(userInput);
    if (preBackendResult.handled) {
      return;
    }

    await handlePropertyFlow(userInput, preBackendResult.effectiveComponent);
  };

  // Auto-send effect: when autoSendCommand sets inputValue, fire handleSendMessage

  return {
    handleSendMessage,
    handleStopGeneration,
    buildStreamingCallbacks,
    applyPropertyChanges,
    handlePlaceComponent,
    handlePlaceComponents,
    handleApplyLayout,
  };
}
